import { AppStoreBase } from './AppStoreBase';
import { IAppComponents } from '../models/IAppComponents';
import { computed, observable, action } from 'mobx';
import { StoreContext } from '../../../configuration/StoreContext';
import { Manifest } from '../../../configuration/Manifest';
import { TGetUserByTokenResponseBody } from '@kurtosys/ksys-api-client/dist/models/requests/auth/TGetUserByTokenResponseBody';
import { TUserProfileAction } from '../../../models/commonTypes';
import { ICardProps } from '@kurtosys/ksys-app-components/dist/components/base/Card/models';
import { IGrid } from '@kurtosys/ksys-app-components/dist/components/base/Grid/models/IGrid';
import UserDetail from '../../UserDetail/UserDetail';
import Assurance from '../../Assurance/Assurance';
import MfaSetup from '../../MfaSetup/MfaSetup';
import ResetPassword from '../../ResetPassword/ResetPassword';
import Impersonation from '../../Impersonation/Impersonation';

/* [Component: appStoreComponentImport] */

export class AppStore extends AppStoreBase {
	@observable.ref loadingKeys: string[] = [];
	@observable.ref public user: TGetUserByTokenResponseBody | undefined;

	constructor(element: HTMLElement, url: string, storeContext: StoreContext, manifest: Manifest) {
		super(element, url, storeContext, manifest);
		this.getUser();
		this.initialize();
	}

	@computed
	get email(): string | undefined {
		return this.user && this.user.email;
	}

	@computed
	get phone(): string | undefined {
		return this.user && this.user.phone;
	}

	@computed
	get userName(): string | undefined {
		return this.user && this.user.userName;
	}

	@computed
	get clientName(): string | undefined {
		return this.user && this.user.clientName;
	}

	@computed get enabled2FAMethods(): string[] {
		return this.user && this.user.enabled2FAMethods || [];
	}

	@computed
	get hasData(): boolean {
		// TODO: Each Application should put custom show logic here: "return this.storeContext[component store].hasData;"
		return true;
	}

	@computed
	get components(): IAppComponents {
		return {
			userDetail: {
				key: 'userDetail',
				component: UserDetail,
			},
			assurance: {
				key: 'assurance',
				component: Assurance,
			},
			mfaSetup: {
				key: 'mfaSetup',
				component: MfaSetup,
			},
			resetPassword: {
				key: 'resetPassword',
				component: ResetPassword,
			},
			impersonation: {
				key: 'impersonation',
				component: Impersonation,
			},
			/* [Component: appStoreComponent] */
		};
	}

	@computed
	get grid(): IGrid | undefined {
		const appComponentConfiguration = this.getComponentConfiguration('app');
		if (appComponentConfiguration && appComponentConfiguration.grid) {
			return appComponentConfiguration.grid;
		}
	}

	@computed
	get userDetailCardProps(): ICardProps {
		const defaultProps = { title: 'User Details' };
		const cardProps = this.componentConfiguration && this.componentConfiguration.userDetail && this.componentConfiguration.userDetail.cardProps;
		return Object.assign(defaultProps, cardProps);
	}

	@computed
	get resetPasswordCardProps(): ICardProps {
		const defaultProps = { title: 'Reset Password' };
		const cardProps = this.componentConfiguration && this.componentConfiguration.resetPassword && this.componentConfiguration.resetPassword.cardProps;
		return Object.assign(defaultProps, cardProps);
	}

	@computed
	get mfaSetupCardProps(): ICardProps {
		const defaultProps = { title: 'Two-Factor Authentication' };
		const cardProps = this.componentConfiguration && this.componentConfiguration.mfaSetup && this.componentConfiguration.mfaSetup.cardProps;
		return Object.assign(defaultProps, cardProps);
	}

	get assuranceSetupCardProps(): ICardProps {
		const defaultProps = { title: 'Assurance' };
		const cardProps = this.componentConfiguration && this.componentConfiguration.assurance && this.componentConfiguration.assurance.cardProps;
		return Object.assign(defaultProps, cardProps);
	}

	@computed
	get impersonationCardProps(): ICardProps {
		const defaultProps = { title: 'Impersonation' };
		const cardProps = this.componentConfiguration && this.componentConfiguration.impersonation && this.componentConfiguration.impersonation.cardProps;
		return Object.assign(defaultProps, cardProps);
	}

	@computed
	get disabledActions() {
		const disabledActions = this.appComponentConfiguration && this.appComponentConfiguration.disabledActions;
		return new Set<TUserProfileAction>(disabledActions);
	}

	public isActionEnabled = (action: TUserProfileAction) => {
		return !this.disabledActions.has(action);
	}

	@action
	getUser = async () => {
		if (!this.user) {
			const { kurtosysApiStore } = this.storeContext;
			this.user = await kurtosysApiStore.getUserByToken.execute();
			if (this.user.userName) {
				this.storeContext.assuranceStore.checkUsernameForAssurance(this.user.userName);
			}
			this.storeContext.userDetailStore.initialize();
			this.storeContext.impersonationStore.initialize();
		}
		return this.user;
	}

	@action
	refreshUser = async () => {
		const { kurtosysApiStore } = this.storeContext;
		this.user = await kurtosysApiStore.getUserByToken.execute();
		if (this.user.userName) {
			this.storeContext.assuranceStore.checkUsernameForAssurance(this.user.userName);
		}
		this.storeContext.userDetailStore.initialize();
		this.storeContext.impersonationStore.initialize();
	}

	startLoading = (key: string) => {
		this.loadingKeys = [...this.loadingKeys, key];
	}

	@action
	stopLoading = (key: string) => {
		const firstIndex = this.loadingKeys.indexOf(key);
		this.loadingKeys = this.loadingKeys.filter((value, index) => {
			return index !== firstIndex;
		});
	}
}